import { createRef, forwardRef, useContext, useState } from "react";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import SimpleSlideshow from "../SimpleSlideshow";
import BjelinButton from "../BjelinButton";
import ImageLoader from "../ImageLoader";
import placeholderImage from "../../assets/placeholder-product-image.svg";
import defaultPlaceholderImage from "../../assets/placeholder-product-image.svg";
import {
  productIsPerSquareMeter,
  productIsPurchasable,
  productPriceUnit,
  productStockStatus,
  useProductUrl
} from "../../utils/ProductUtils";
import FormattedPrice from "../FormattedPrice";
import IconsOrTagline from "../IconsOrTagline";
import { CartContext } from "../../functional-components/CartContext";
import { divide } from "../../utils/NumberUtils";
import { addVat } from "../../utils/VatUtils";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import Icon from "../Icon";
import StockStatus from "../StockStatus";
import useShowPricesWithVat from "../../hooks/useShowPricesWithVat";
import { Flags } from "../Flags";
import "./style.scss";

const motionVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1
  },
  out: {
    opacity: 0,
    scale: 0.8
  }
};

const ProductPreview = ({
  product,
  preferredCategory,
  layout = "card",
  showCartButton = false,
  dontExpandCart = false,
  noSlideShow = false,
  pkey,
  clickToFilter = false,
  b2b = false
}) => {
  const features = useSiteFeatures();
  const tp = useProductTranslations();
  const t = useBjelinTranslations();
  const showPricesWithVat = useShowPricesWithVat();
  const [playing, _setPlaying] = useState(false);
  const setPlaying = play => () => _setPlaying(play);
  const to = useProductUrl(product, preferredCategory);
  const { loggedAddToCart, findItem } = useContext(CartContext);

  const {
    name,
    tagline,
    images,
    unitQuantityFactor,
    price = {},
    specsIndexed,
    sku
  } = product;
  const isPerSquareMeter = productIsPerSquareMeter(unitQuantityFactor);
  const priceUnit = productPriceUnit(unitQuantityFactor);
  const {
    basePrice,
    unitPrice: unitBasePrice,
    previousBasePrice,
    currency
  } = price || {
    basePrice: null,
    unitPrice: null,
    previousBasePrice: null,
    currency: null
  };
  const previousPrice = previousBasePrice
    ? addVat(
        isPerSquareMeter
          ? divide(previousBasePrice, unitQuantityFactor)
          : previousBasePrice
      )
    : false;
  const hasPrice = features.prices() && !!basePrice;
  const hasPreviousPrice = features.prices() && !!previousPrice;

  const inCart = findItem(sku);
  const stockStatus = productStockStatus(product, inCart, tp, b2b);

  const addToCartHandler = e => {
    loggedAddToCart(product, 1, dontExpandCart, b2b);
    e.preventDefault();
  };

  return (
    <motion.div
      layout={"position"}
      key={pkey}
      data-sku={sku}
      className={classNames("product-preview", "layout-" + layout, {
        withCartButton: showCartButton
      })}
      onMouseEnter={setPlaying(true)}
      onMouseLeave={setPlaying(false)}
      onTouchStart={setPlaying(true)}
      onTouchEnd={setPlaying(false)}
      variants={motionVariants}
      initial="hidden"
      animate="visible"
      exit="out"
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
    >
      <Link to={to}>
        <div className={"section1"}>
          <ImageLoader
            className={"productPreloadImage"}
            containerClassName={"productImageContainer"}
            imageUrl={images[0].styles.preview}
          >
            {noSlideShow ? (
              <>
                <div
                  className="productPreviewImage"
                  style={{
                    backgroundImage: `url("${images[0].styles.preview}")`
                  }}
                >
                  <Flags product={product} inline={false} />
                </div>
              </>
            ) : (
              <SimpleSlideshow
                className={"productPreviewImage"}
                label={name}
                images={images}
                playing={playing}
                rewindOnPause={true}
              >
                <Flags product={product} inline={false} />
              </SimpleSlideshow>
            )}
          </ImageLoader>
        </div>
        <div className={"section2"}>
          <h4>
            {b2b && <span className="text-pale">{sku} </span>}
            <span>{name}</span>
          </h4>
          {hasPrice && (
            <div className={"price"}>
              {isPerSquareMeter && !!unitBasePrice ? (
                <FormattedPrice
                  price={
                    showPricesWithVat ? addVat(unitBasePrice) : unitBasePrice
                  }
                  price_unit={priceUnit}
                  currency={currency}
                />
              ) : (
                <FormattedPrice
                  price={showPricesWithVat ? addVat(basePrice) : basePrice}
                  sales_unit={priceUnit}
                  currency={currency}
                />
              )}
            </div>
          )}
          {hasPreviousPrice && (
            <div className={"previous-unit-price"}>
              {tp("previous_unit_price")}
              <FormattedPrice price={previousPrice} currency={currency} />
            </div>
          )}
        </div>
        <div className={"section3"}>
          {showCartButton && productIsPurchasable(product, b2b) ? (
            inCart && inCart.amount ? (
              <div
                style={{
                  padding: "15px",
                  fontWeight: 700
                }}
                title={t("cart.existing.product", { amount: inCart.amount })}
              >
                <Icon name="check" /> {inCart.amount}{" "}
                {tp(
                  isPerSquareMeter
                    ? "price_unit.per_package"
                    : "sales_unit.per_package"
                )}
              </div>
            ) : (
              <BjelinButton
                color="bjelin-green"
                block={false}
                lg={false}
                className={"mb-3"}
                icon="cart-plus"
                onClick={addToCartHandler}
              >
                {tp("add_to_cart.short")}
              </BjelinButton>
            )
          ) : (
            <div className={"specs"}>
              <IconsOrTagline
                key="icons"
                specsIndexed={specsIndexed}
                showLabel={layout !== "card"}
                sku={sku}
                clickToFilter={clickToFilter}
                tagline={tagline}
              />
            </div>
          )}
          {features.shop() && (
            <StockStatus
              stockStatus={stockStatus}
              sku={sku}
              key="stock-status"
            />
          )}
        </div>
      </Link>
    </motion.div>
  );
};

ProductPreview.propTypes = {
  product: PropTypes.object.isRequired,
  layout: PropTypes.oneOf(["card", "list"]),
  showCartButton: PropTypes.bool,
  dontExpandCart: PropTypes.bool,
  pkey: PropTypes.string,
  clickToFilter: PropTypes.bool,
  hideIcons: PropTypes.bool,
  noSlideShow: PropTypes.bool,
  b2b: PropTypes.bool
};

export default ProductPreview;

export const ProductPreviewPlaceholder = forwardRef(({ product }, ref) => {
  ref = ref || createRef();
  const { name } = product;

  return (
    <div className="render-placeholder product-preview" ref={ref}>
      <div className={"section1"}>
        <div
          className="image-placeholder"
          style={{ backgroundImage: `url('${defaultPlaceholderImage}')` }}
        />
      </div>
      <div className={"section2"}>
        <h4>{name}</h4>
        <div className="price" />
      </div>
      <div className={"section3"}>
        <div className="tagline" />
      </div>
    </div>
  );
});
ProductPreviewPlaceholder.displayName = "ProductPreviewPlaceholder";

export function ProductPreviewLoadingPlaceholder({ layout }) {
  const style = {
    backgroundImage: "url(" + placeholderImage + ")"
  };

  const layoutClass = layout ? "layout-" + layout : "layout-card";

  return (
    <motion.div
      className={classNames("product-preview", "placeholder", layoutClass)}
    >
      <div className={"section1"}>
        <div
          className="productPreviewImage"
          role="img"
          aria-label="Placeholder Product Image"
          style={style}
        />
      </div>
      <div className={"section2"}>
        <div className={"flags"} />
        <h4 />
        <div className="price" />
      </div>
      <div className={"section3"}>
        <div className="tagline" />
      </div>
    </motion.div>
  );
}
